import chroma from "chroma-js";

import {
  PALETTE_SCHEMES,
  paletteIds,
  paletteOf,
} from "../orion/config/dashboards";
import {
  ESTATE_PALETTE,
  loadCustomPalettes,
  setPaletteEverywhere,
  setPaletteFor,
  sharedPalette,
} from "../components/forge/components/cardPalette";

/**
 * Colour, as something that can be asked for.
 * ---------------------------------------------------------------------------
 * The picker can already do all of this: choose a shared palette, build one
 * from a colour and a scheme, edit the ten swatches one at a time, take one
 * off a picture, and push the result onto every page and every board. None of
 * it could be ASKED for, because the only way in was ten controls in a popover.
 *
 * What someone says is "four magenta colours and one orange", which is not a
 * hex value, a scheme or a swatch index - it is a list of colour names with
 * counts. So that is what the tool takes, and the work of turning five names
 * into a palette that still reads as a ramp is done here rather than demanded
 * of whoever is asking:
 *
 *   - a name becomes a hex through the table below, which holds the words
 *     people actually use rather than the CSS list in full;
 *   - a colour asked for more than once becomes that many DISTINCT shades of
 *     itself, walked from light to dark, because "four magenta colours" means
 *     four of them and not the same one four times;
 *   - the result is padded to ten, since a palette is ten swatches everywhere
 *     else in the product - the padding is more shades of the colours already
 *     asked for, so the ten never introduce a hue nobody asked for.
 *
 * `scope` is where it lands, and it defaults to everywhere, because a request
 * that says "for all the pages" should not need to also say it in a second
 * argument.
 * ---------------------------------------------------------------------------
 */

const text = (s) => ({ content: [{ type: "text", text: String(s) }] });

/* The words, not the CSS list.
   -------------------------------------------------------------------------
   CSS has 148 named colours, most of which nobody asks for by name and some
   of which ("linen", "oldlace") would be chosen by accident on a near-miss.
   These are the ones a person names when they mean a colour, each pinned to a
   value that reads as that colour on both grounds rather than to whatever the
   CSS list happens to call it - CSS "purple" is nearly black on a dark page. */
const NAMED = {
  magenta: "#e0218a", fuchsia: "#e0218a", pink: "#ec4899", rose: "#f43f5e",
  orange: "#f97316", amber: "#f59e0b", gold: "#eab308", yellow: "#facc15",
  red: "#ef4444", crimson: "#dc2626", maroon: "#991b1b", coral: "#fb7185",
  purple: "#a855f7", violet: "#8b5cf6", indigo: "#6366f1", lavender: "#c4b5fd",
  blue: "#3b82f6", azure: "#0ea5e9", sky: "#38bdf8", navy: "#1e3a8a",
  cyan: "#06b6d4", teal: "#14b8a6", turquoise: "#2dd4bf", mint: "#6ee7b7",
  green: "#22c55e", emerald: "#10b981", lime: "#84cc16", forest: "#166534",
  olive: "#65a30d", brown: "#92400e", tan: "#d6b48c", peach: "#fdba74",
  grey: "#6b7280", gray: "#6b7280", slate: "#64748b", steel: "#475569",
  silver: "#cbd5e1", white: "#f8fafc", black: "#111827", charcoal: "#1f2937",
};

export const colorNames = () => Object.keys(NAMED);

const HEX = /^#?[0-9a-f]{6}$|^#?[0-9a-f]{3}$/i;

/** One entry of the list: "magenta", "#ff00ff", "4 magenta", "magenta x4". */
function readWish(entry) {
  const said = String(entry || "").trim().toLowerCase();
  if (!said) return null;

  /* A count, however it was written. "4 magenta", "magenta x4", "magenta * 4"
     and "four magenta" all say the same thing, and a request phrased in words
     rather than digits is the likelier one when a person is talking. */
  const WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };
  let how = 1;
  let what = said;

  const lead = said.match(/^(\d+|one|two|three|four|five|six|seven|eight|nine|ten)\s+(.+)$/);
  const tail = said.match(/^(.+?)\s*[x*]\s*(\d+)$/);
  if (lead) { how = Number(lead[1]) || WORDS[lead[1]] || 1; what = lead[2]; }
  else if (tail) { how = Number(tail[2]) || 1; what = tail[1]; }

  what = what.replace(/\b(colou?rs?|shades?|tints?|tones?)\b/g, "").trim();
  how = Math.min(Math.max(how, 1), 10);

  if (HEX.test(what)) return { hex: chroma(what.startsWith("#") ? what : `#${what}`).hex(), how };

  const name = Object.keys(NAMED).find(k => what === k)
    || Object.keys(NAMED).find(k => what.includes(k));
  return name ? { hex: NAMED[name], how, name } : null;
}

/**
 * The wishes, spread into swatches.
 *
 * A colour asked for n times becomes n shades of itself, walked from light to
 * dark across a band that keeps all of them recognisably that colour - a
 * "magenta" at 0.86 lightness is pink and at 0.16 is nearly black, so the band
 * is the middle of the range rather than the whole of it.
 */
export function swatchesFrom(wishes) {
  const out = [];
  wishes.forEach(({ hex, how }) => {
    const seed = chroma(hex);
    const h = seed.get("hsl.h") || 0;
    const s = Math.min(Math.max(seed.get("hsl.s") || 0.7, 0.45), 0.95);
    const mid = seed.get("hsl.l") || 0.55;
    for (let i = 0; i < how; i += 1) {
      /* one colour asked for once keeps exactly the colour it was asked for */
      const l = how === 1 ? mid : 0.72 - (0.40 * i) / (how - 1);
      out.push((how === 1 ? seed : chroma.hsl(h, s, l)).hex());
    }
  });
  return out;
}

/** Ten, without letting a hue in that nobody asked for. */
function toTen(colors) {
  if (!colors.length) return colors;
  const out = [...colors];
  let round = 1;
  while (out.length < 10) {
    const from = chroma(colors[(out.length - colors.length) % colors.length]);
    // each pass round the list is a little darker than the one before it
    out.push(from.darken(0.45 * round).hex());
    if ((out.length - colors.length) % colors.length === 0) round += 1;
  }
  return out.slice(0, 10);
}

/**
 * A palette from what was said.
 *
 * @param said   a list of wishes - names, hexes, or either with a count
 * @param name   what to call it, so it is findable in the picker afterwards
 */
export function paletteFromWords(said, name) {
  const wishes = (Array.isArray(said) ? said.map(readWish).filter(Boolean) : readSentence(said));
  if (!wishes.length) return null;

  const asked = swatchesFrom(wishes);
  return {
    base: asked[0],
    scheme: "mono",
    name: (name || wishes.map(w => (w.how > 1 ? `${w.how} ${w.name || "colours"}` : (w.name || "colour"))).join(" + ")).slice(0, 40),
    colors: toTen(asked),
    asked,
  };
}

/**
 * A whole sentence, read for colours.
 *
 * The list form is what a caller with structure hands over; this is for the
 * one that has only what somebody said - "...that will contain 4 magenta
 * colors and one orange color". Splitting that on commas and "and" gets the
 * second half right and the first half wrong, because the colour is at the end
 * of a long clause with a count in front of it.
 *
 * So the sentence is scanned rather than split: every colour word this table
 * knows, in the order it appears, with the count that immediately precedes it
 * if there is one. Nothing else in the sentence has to parse, which is the
 * point - the sentence is mostly not about colour.
 */
function readSentence(said) {
  const words = String(said || "").toLowerCase();
  const names = colorNames().sort((a, b) => b.length - a.length).join("|");
  const re = new RegExp(
    `(?:(\\d+|one|two|three|four|five|six|seven|eight|nine|ten)\\s+(?:\\w+\\s+){0,2}?)?\\b(${names})\\b`,
    "g",
  );

  const out = [];
  const seen = new Set();
  let m = re.exec(words);
  while (m) {
    const wish = readWish(`${m[1] ? `${m[1]} ` : ""}${m[2]}`);
    /* the same colour named twice in one sentence is one wish, not two - "4
       magenta colors" and a later "magenta" are the same four */
    if (wish && !seen.has(wish.name)) { seen.add(wish.name); out.push(wish); }
    m = re.exec(words);
  }
  return out;
}

const SCOPES = ["wiki", "discovery", "apps"];
const PAGE_OF = { wiki: "the wiki", discovery: "discovery", apps: "applications" };

export default function paletteTools(say) {
  const shared = () => {
    const v = sharedPalette();
    return v === ESTATE_PALETTE ? "the estate's own tints" : (paletteOf(v).label || "custom");
  };

  return [
    {
      name: "list_palettes",
      description: "The palettes that can be chosen for the card pages and the dashboards: "
        + "the shared ones by name, the ones someone has made, and the colour names set_palette understands. "
        + "Also says what is in use now.",
      inputSchema: { type: "object", properties: {} },
      async execute() {
        const made = loadCustomPalettes();
        return text(JSON.stringify({
          in_use_everywhere: shared(),
          shared_palettes: paletteIds(),
          saved_palettes: made.map(p => p.name || p.base),
          schemes: PALETTE_SCHEMES.map(s => s.id),
          colour_names: colorNames(),
          note: "set_palette takes colour names with counts, e.g. [\"4 magenta\", \"1 orange\"].",
        }, null, 2));
      },
    },

    {
      name: "set_palette",
      description: "Set the colours the cards and the dashboards are painted in. "
        + "Give `colors` as the colours wanted - a name, a hex, or a name with a count "
        + "(\"4 magenta\", \"1 orange\") - or `palette` to choose a shared palette by name. "
        + "Applies to every page and every dashboard unless `scope` names one page.",
      inputSchema: {
        type: "object",
        properties: {
          colors: {
            type: "array",
            items: { type: "string" },
            description: "The colours wanted, e.g. [\"4 magenta\", \"1 orange\"]. Counts are honoured: "
              + "a colour asked for four times becomes four distinct shades of it.",
          },
          palette: { type: "string", description: "A shared palette by name, from list_palettes." },
          name: { type: "string", description: "What to call the palette this makes." },
          scope: {
            type: "string",
            enum: ["everywhere", ...SCOPES],
            description: "everywhere (the default - all card pages and all dashboards), or one page.",
          },
        },
      },
      async execute({ colors, palette, name, scope = "everywhere" } = {}) {
        let next;
        let made = null;

        if (palette && !colors?.length) {
          const known = paletteIds();
          const hit = known.find(p => p.toLowerCase() === String(palette).toLowerCase())
            || known.find(p => p.toLowerCase().includes(String(palette).toLowerCase()));
          if (!hit) return text(`There is no palette called "${palette}". There is: ${known.join(", ")}.`);
          next = hit;
        } else {
          made = paletteFromWords(colors, name);
          if (!made) {
            return text("I could not read any colour in that. Give colour names or hex values, "
              + `for instance ["4 magenta", "1 orange"]. The names I know: ${colorNames().join(", ")}.`);
          }
          next = { base: made.base, scheme: made.scheme, name: made.name, colors: made.colors };
        }

        let where;
        if (scope === "everywhere") {
          const boards = await setPaletteEverywhere(next);
          where = `every card page${boards ? ` and ${boards} dashboard${boards === 1 ? "" : "s"}` : ""}`;
        } else {
          setPaletteFor(scope, next);
          where = PAGE_OF[scope] || scope;
        }

        const what = made
          ? `${made.name} - ${made.asked.join(", ")}${made.colors.length > made.asked.length
            ? ` (filled out to ten with shades of those)` : ""}`
          : next;
        say?.(`Palette: ${what}`);
        return text(`Painted ${where} in ${what}.`);
      },
    },

    {
      name: "reset_palette",
      description: "Put the colours back to the estate's own tints.",
      inputSchema: {
        type: "object",
        properties: { scope: { type: "string", enum: ["everywhere", ...SCOPES] } },
      },
      async execute({ scope = "everywhere" } = {}) {
        if (scope === "everywhere") await setPaletteEverywhere(ESTATE_PALETTE);
        else setPaletteFor(scope, ESTATE_PALETTE);
        return text(`${scope === "everywhere" ? "Every card page is" : `${PAGE_OF[scope]} is`} back on the estate's own tints.`);
      },
    },
  ];
}
